import * as Immutable from 'immutable';
import { DATA_VERSION } from './config';
import UserDataStore from './UserData/_stores/UserDataStore';
import UIDataStore from './UIData/_stores/UIDataStore';
import AppDataStore from './AppData/_stores/AppDataStore';
import AuthStore from './Auth/_stores/AuthStore';
import ViewProductStore from './AppData/_stores/ViewProductStore';
import PriceStore from './UIData/_stores/PriceStore';
import { configureStore } from '@reduxjs/toolkit';
import type { Action } from '@reduxjs/toolkit';
import AjaxMiddleware from './Common/_middlewares/AjaxMiddleware';
import WebsiteReducer from './WebsiteReducer';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import type { StoreEnhancer } from 'redux';

// TODO: Fix types for interface entries across all stores to avoid using Immutable types as for most usages they're not converted yet

export interface IWebsiteStore {
	userData: UserDataStore
	UIData: UIDataStore
	Auth: AuthStore
	appData: AppDataStore
	version: string
}

export const WebsiteStoreFactory = Immutable.Record<IWebsiteStore>({
	userData: new UserDataStore,
	UIData: new UIDataStore,
	Auth: new AuthStore,
	appData: new AppDataStore,
	version: DATA_VERSION,
});

const getStoreObjectValue = (storeObject: object, key: string): unknown => {
	if (Immutable.isMap(storeObject)) {
		return storeObject.get(key);
	}

	return Object.prototype.hasOwnProperty.call(storeObject, key)
		? (storeObject as Record<string, unknown>)[key]
		: undefined;
};

export default class WebsiteStore extends WebsiteStoreFactory implements IWebsiteStore {
	constructor(storeObject:object = {}) {
		let store = Immutable.Map({
			UIData: new UIDataStore,
			userData: new UserDataStore,
			appData: new AppDataStore,
			Auth: new AuthStore,
			version: DATA_VERSION
		});

		store = Immutable.mergeDeep(store, Immutable.fromJS(storeObject, (key, value, path) => {
			if(path[0] === "appData") {
				if(path[1] === 'viewProducts') {
					if(['price','starting_wholesale_price','starting_set_price'].includes(String(key))) {
						return new PriceStore(value.toJS() as any);
					}
				}

				if(path[1] === 'products') {
					if(['price_retail','price_wholesale','price_dropship'].includes(String(key))) {
						return new PriceStore(value.toJS() as any);
					}
				}

				if(path[1] === 'stockProducts') {
					if(['price'].includes(String(key))) {
						return new PriceStore(value.toJS() as any);
					}
				}

				if(path[1] === 'brandingOptionTypes') {
					if(key === 'price') {
						return new PriceStore(value.toJS() as any);
					}
				}
			}

			return Immutable.isKeyed(value) ? value.toOrderedMap() : value.toList();
		}));

		const userData = getStoreObjectValue(storeObject, 'userData');
		if (userData !== undefined) {
			store = store.set('userData', new UserDataStore(userData));
		}

		super(store);
	}
}

type SentryReduxState = Omit<Partial<IWebsiteStore>, 'appData'>;
type SentryReduxAction = Action & Record<string, unknown>;
const APP_DATA_EDIT_ACTION_TYPE = 'APP_DATA_EDIT';

const sanitizeSentryReduxState = (state?: WebsiteStore): SentryReduxState | null => {
	if (!state) return null;

	const sentryState = state.toObject() as Partial<IWebsiteStore>;
	delete sentryState.appData;

	return sentryState;
}

const sanitizeSentryReduxAction = (action: SentryReduxAction): SentryReduxAction | null => {
	if (action.type === APP_DATA_EDIT_ACTION_TYPE) {
		return {type: action.type};
	}

	return action;
}

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
	stateTransformer: sanitizeSentryReduxState,
	actionTransformer: sanitizeSentryReduxAction,
}) as StoreEnhancer;

interface DevToolsList {
	take: (count: number) => unknown
	filter: (predicate: (value: unknown, key: unknown) => boolean) => unknown
}

const canTakeDevToolsList = (list: unknown): list is Pick<DevToolsList, 'take'> => (
	typeof list === 'object'
	&& list !== null
	&& 'take' in list
	&& typeof list.take === 'function'
);

const canFilterDevToolsList = (list: unknown): list is Pick<DevToolsList, 'filter'> => (
	typeof list === 'object'
	&& list !== null
	&& 'filter' in list
	&& typeof list.filter === 'function'
);

const takeDevToolsList = (state: WebsiteStore, path: Array<string|number>, count = 1) => {
	const list = state.getIn(path);

	if (canTakeDevToolsList(list)) {
		state.setIn(path, list.take(count));
	}
}

const filterDevToolsList = (state: WebsiteStore, path: Array<string|number>, keys: string[]) => {
	const list = state.getIn(path);

	if (canFilterDevToolsList(list)) {
		state.setIn(path, list.filter((value: unknown, key: unknown) => keys.includes(String(key))));
	}
}

const sanitizePaginationList = (state: WebsiteStore, path: Array<string|number>) => {
	takeDevToolsList(state, [...path, 'data']);
	takeDevToolsList(state, [...path, 'itemLists']);
}

const isBrowser = typeof document !== 'undefined';
const initialStore = isBrowser ? new WebsiteStore(window?.PRELOADED_STORE || {}) : new WebsiteStore({});

export const store = configureStore<WebsiteStore, Action>({
	reducer: WebsiteReducer,
	preloadedState: initialStore,
	middleware: (getDefaultMiddleware) => getDefaultMiddleware({
		immutableCheck: false,
		actionCreatorCheck: true,
		serializableCheck: false,
		thunk: false,
	}).concat(AjaxMiddleware),
	enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
	devTools: {
		//@ts-ignore
		stateSanitizer: (state:WebsiteStore) => state.withMutations(state => {
			//Remove some data from the redux devtools so it's actually usable
			takeDevToolsList(state, ['appData', 'countries']);
			takeDevToolsList(state, ['appData', 'gpsrManufacturers']);
			takeDevToolsList(state, ['appData', 'gpsrContacts']);
			filterDevToolsList(state, ['appData', 'products'], ['129']);
			takeDevToolsList(state, ['appData', 'productCategories']);
			takeDevToolsList(state, ['appData', 'productSubCategories']);
			takeDevToolsList(state, ['appData', 'productVariantTypes']);
			state.setIn(['UIData', 'designLab','patternDesignService','categories'], '%CATEGORIES%');
			state.setIn(['UIData', 'designLab','patternDesignService','patterns'], '%PATTERNS%');
			filterDevToolsList(state, ['appData', 'viewCategories'], ['1']);
			filterDevToolsList(state, ['appData', 'viewProducts'], ['141']);
			filterDevToolsList(state, ['appData', 'viewProductAttributeValues'], ['1']);
			takeDevToolsList(state, ['appData', 'viewProductAttributeTypes']);
			takeDevToolsList(state, ['appData', 'stockProducts']);
			takeDevToolsList(state, ['appData', 'shippingTypes']);
			takeDevToolsList(state, ['appData', 'storeIntegrations']);
			takeDevToolsList(state, ['appData', 'brandingOptionTypes']);
			takeDevToolsList(state, ['appData', 'goals']);
			takeDevToolsList(state, ['appData', 'goalCategories']);
			takeDevToolsList(state, ['appData', 'rewards']);
			takeDevToolsList(state, ['userData', 'goals']);
			takeDevToolsList(state, ['userData', 'rewards']);
			takeDevToolsList(state, ['userData', 'collections']);
			takeDevToolsList(state, ['userData', 'brandingOptions']);
			takeDevToolsList(state, ['userData', 'taxNumbers']);
			takeDevToolsList(state, ['userData', 'storePresets']);
			takeDevToolsList(state, ['userData', 'storePages']);
			takeDevToolsList(state, ['userData', 'stores']);
			sanitizePaginationList(state, ['userData', 'addresses']);
			sanitizePaginationList(state, ['userData', 'creditCards']);
			sanitizePaginationList(state, ['userData', 'orders']);
			sanitizePaginationList(state, ['userData', 'images']);
			sanitizePaginationList(state, ['userData', 'productDesigns']);
			sanitizePaginationList(state, ['userData', 'photoLabCreations']);
		})
	}
});

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppStore = typeof store
